import OnboardingSurvey from "#web/components/onboarding/OnboardingSurvey/OnboardingSurvey";
import { validateEmail } from "#web/utils/validateEmail";
import { BlockStack, Box, Card, Checkbox, TextField } from "@shopify/polaris";
import { useCallback, useState } from "react";

export const Onboarding = () => {
  const [answers, setAnswers] = useState<{
    email: string;
    installReason: string;
    referrer: null | string;
    revenue: null | string;
  }>({
    email: "",
    installReason: "",
    referrer: null,
    revenue: null,
  });

  const setRevenue = useCallback(
    (revenue: string) => {
      setAnswers({ ...answers, revenue });
    },
    [answers],
  );

  const setInstallReason = useCallback(
    (installReason: string) => {
      setAnswers({ ...answers, installReason });
    },
    [answers],
  );

  const setReferrer = useCallback(
    (referrer: string) => {
      setAnswers({ ...answers, referrer });
    },
    [answers],
  );

  const setEmail = useCallback(
    (email: string) => {
      setAnswers({ ...answers, email });
    },
    [answers],
  );

  return (
    <OnboardingSurvey answers={answers}>
      <OnboardingSurvey.Question subtitle="" title="How did you hear about KeepCart?">
        <ReferrerQuestion answer={answers.referrer} setAnswer={setReferrer} />
      </OnboardingSurvey.Question>
      <OnboardingSurvey.Question
        subtitle="Select an option for your revenue. This information is used internally. We never share it!"
        title="What's your monthly revenue?"
      >
        <RevenueQuestion answer={answers.revenue} setAnswer={setRevenue} />
      </OnboardingSurvey.Question>
      <OnboardingSurvey.Question
        subtitle="Explain the reason you installed KeepCart"
        title="Why did you install KeepCart?"
      >
        <InstallReason answer={answers.installReason} setAnswer={setInstallReason} />
      </OnboardingSurvey.Question>
      <OnboardingSurvey.Question
        subtitle="To notify you of app updates/issues, discounts, beta features, etc."
        title="What is your email?"
      >
        <ContactEmail setAnswer={setEmail} />
      </OnboardingSurvey.Question>
    </OnboardingSurvey>
  );
};

interface ContactEmailProps {
  setAnswer: (answer: string) => void;
}

const ContactEmail = ({ setAnswer }: ContactEmailProps) => {
  const [email, setEmail] = useState("");

  const handleChange = useCallback((newValue: string) => {
    setEmail(newValue);
    if (validateEmail(newValue)) {
      setAnswer(newValue);
    } else {
      setAnswer("");
    }
  }, []);

  return (
    <TextField
      autoComplete="email"
      label=""
      onChange={handleChange}
      placeholder="you@email.com"
      type="email"
      value={email}
    />
  );
};

interface InstallReasonProps {
  answer: string;
  setAnswer: (answer: string) => void;
}

const InstallReason = ({ answer, setAnswer }: InstallReasonProps) => {
  const handleChange = useCallback((newValue: string) => {
    setAnswer(newValue);
  }, []);

  return (
    <TextField
      autoComplete="off"
      label=""
      multiline={4}
      onChange={handleChange}
      placeholder="Why you installed KeepCart..."
      value={answer}
    />
  );
};

interface ReferrerOptionProps {
  checked: boolean;
  handleChange: (answer: string) => void;
  title: string;
}

const ReferrerOption = ({ checked, handleChange, title }: ReferrerOptionProps) => (
  <Card background={checked ? "bg-surface-secondary" : undefined} padding="0">
    <Box paddingBlock="300" paddingInline="400">
      <Checkbox checked={checked} label={title} onChange={() => handleChange(title)} />
    </Box>
  </Card>
);

interface ReferrerQuestionProps {
  answer: null | string;
  setAnswer: (answer: string) => void;
}

const ReferrerQuestion = ({ answer, setAnswer }: ReferrerQuestionProps) => {
  const handleChange = useCallback((checkedAnswer: string) => {
    setAnswer(checkedAnswer);
  }, []);

  const answers = [
    "Newsletter",
    "Shopify app store",
    "Email",
    "Twitter",
    "Shopify forum",
    "Word of mouth",
    "Google search",
    "Other",
  ];

  return (
    <BlockStack gap="200">
      {answers.map((_answer) => (
        <ReferrerOption
          checked={answer === _answer}
          handleChange={() => {
            handleChange(_answer);
          }}
          key={_answer}
          title={_answer}
        />
      ))}
    </BlockStack>
  );
};

interface RevenueOptionProps {
  checked: boolean;
  handleChange: (checkedAnswer: string) => void;
  title: string;
}

const RevenueOption = ({ checked, handleChange, title }: RevenueOptionProps) => (
  <Card background={checked ? "bg-surface-secondary" : undefined} padding="0">
    <Box paddingBlock="300" paddingInline="400">
      <Checkbox checked={checked} label={title} onChange={() => handleChange(title)} />
    </Box>
  </Card>
);

interface RevenueQuestionProps {
  answer: null | string;
  setAnswer: (answer: string) => void;
}

const RevenueQuestion = ({ answer, setAnswer }: RevenueQuestionProps) => {
  const handleChange = useCallback((checkedAnswer: string) => {
    setAnswer(checkedAnswer);
  }, []);

  const answers = [
    "Just starting out",
    "$5k - 20k",
    "$20k - $100k",
    "$100k - $500k",
    "$500k - $1mm",
    "$1mm - $5mm",
    "$5mm+",
  ];

  return (
    <BlockStack gap="200">
      {answers.map((_answer) => (
        <RevenueOption
          checked={answer === _answer}
          handleChange={() => {
            handleChange(_answer);
          }}
          key={_answer}
          title={_answer}
        />
      ))}
    </BlockStack>
  );
};
